import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { take } from 'rxjs';

import { latestBookableDate, pickerMaxDate } from '../../../../../../helpers/meeting-dates';
import { MeetingService } from '../../../../../../services/meeting.service';
import { StudentService, CounselorStudent } from '../../../../../../services/student.service';

export type CounselorMeetingsCreateResult = { action: 'created' } | undefined;

type CreateView = 'create' | 'confirm';

const DESCRIPTION_LIMIT = 1000;

/**
 * Lets a counselor book a meeting with a student in their network. Two views:
 * the form, then a confirmation step that can hand the counselor back to the
 * form rather than making them cancel and start over.
 */
@Component({
  selector: 'app-counselor-meetings-create',
  imports: [
    ReactiveFormsModule,
    DatePipe,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatIconModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './counselor-meetings-create.html',
  styleUrl: './counselor-meetings-create.css',
})
export class CounselorMeetingsCreate implements OnInit {
  private fb = inject(FormBuilder);
  private meetingService = inject(MeetingService);
  private studentService = inject(StudentService);
  protected dialogRef = inject(
    MatDialogRef<CounselorMeetingsCreate, CounselorMeetingsCreateResult>,
  );

  protected view: CreateView = 'create';
  // Signals because submit()'s error handler sets these from inside an HTTP
  // subscribe callback — under zoneless change detection a plain field
  // assigned there never triggers a re-render, so the error would silently
  // never appear.
  protected submitting = signal(false);
  protected submitFailed = signal(false);
  /** The server's reason for the failure, when it gave one worth showing. */
  protected submitError = signal('');
  protected students: CounselorStudent[] = [];

  protected readonly descriptionLimit = DESCRIPTION_LIMIT;
  protected readonly durations = [15, 30, 45, 60];

  /** Today at midnight — a meeting can't be booked into the past. */
  protected readonly minDate = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  /** Upper bound on what's actually bookable — enforced via dateFilter below. */
  protected readonly maxDate = latestBookableDate();

  /** Structural bound fed to the picker's [max]; see pickerMaxDate() for why
   *  this has to be wider than maxDate. */
  protected readonly pickerMaxDate = pickerMaxDate();

  /** Enforces the real booking cutoff at the day level, independent of the
   *  wider structural pickerMaxDate above. */
  protected dateFilter = (date: Date | null): boolean => !!date && date <= this.maxDate;

  /** Earliest option in the start-time list — only the hour/minute matter,
   *  the timepicker ignores the date part. */
  protected readonly minStartTime = (() => {
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    return d;
  })();

  protected form = this.fb.group({
    title: this.fb.nonNullable.control<string>('', [Validators.required, Validators.maxLength(80)]),
    studentUserId: this.fb.control<number | null>(null, Validators.required),
    date: this.fb.control<Date | null>(null, Validators.required),
    startTime: this.fb.control<Date | null>(null, Validators.required),
    durationMinutes: this.fb.control<number | null>(null, Validators.required),
    meetingType: this.fb.nonNullable.control<'remote' | 'in-person'>(
      'in-person',
      Validators.required,
    ),
    // Unvalidated for now: the counselor pastes whatever their calendar or
    // video app gave them, and only remote meetings send it.
    meetingLink: this.fb.nonNullable.control<string>(''),
    description: this.fb.nonNullable.control<string>('', [Validators.maxLength(DESCRIPTION_LIMIT)]),
  });

  ngOnInit(): void {
    this.studentService
      .getMyStudents()
      .pipe(take(1))
      .subscribe({
        next: (students) => (this.students = students),
        error: () => (this.students = []),
      });
  }

  protected displayName(student: CounselorStudent): string {
    const first = student.user.preferred_name || student.user.first_name || '';
    return `${first} ${student.user.last_name || ''}`.trim();
  }

  /** The chosen student, for the confirmation summary. */
  protected get selectedStudent(): CounselorStudent | undefined {
    const id = this.form.controls.studentUserId.value;
    return this.students.find((s) => s.user_id === id);
  }

  protected get descriptionRemaining(): number {
    return DESCRIPTION_LIMIT - (this.form.controls.description.value?.length ?? 0);
  }

  protected get scheduledStart(): Date | null {
    const date = this.form.controls.date.value;
    const time = this.form.controls.startTime.value;
    if (!date || !time) return null;

    const start = new Date(date);
    start.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return start;
  }

  protected goToView(v: CreateView): void {
    this.view = v;
  }

  /** Form → confirmation. Invalid fields are surfaced instead of advancing. */
  protected review(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitFailed.set(false);
    this.view = 'confirm';
  }

  protected submit(): void {
    const start = this.scheduledStart;
    const studentUserId = this.form.controls.studentUserId.value;
    const durationMinutes = this.form.controls.durationMinutes.value;
    if (this.form.invalid || !start || studentUserId === null || durationMinutes === null) return;

    this.submitting.set(true);
    this.submitFailed.set(false);

    this.meetingService
      .createMeeting({
        studentUserId,
        scheduledStart: start,
        durationMinutes,
        title: this.form.controls.title.value,
        description: this.form.controls.description.value,
        meetingType: this.form.controls.meetingType.value,
        meetingLink: this.form.controls.meetingLink.value.trim() || null,
      })
      .pipe(take(1))
      .subscribe({
        next: () => this.dialogRef.close({ action: 'created' }),
        error: (err: HttpErrorResponse) => {
          this.submitting.set(false);
          this.submitFailed.set(true);
          // A rejected slot comes back with a reason worth reading — a buffer
          // clash names the gap needed, a past slot says so directly.
          // Anything else stays generic.
          this.submitError.set(typeof err?.error?.error === 'string' ? err.error.error : '');
        },
      });
  }

  /** Dismisses the create-failure banner without discarding the form. */
  protected dismissError(): void {
    this.submitFailed.set(false);
  }
}
